import {isSettingActive} from './utils';

/**
 * Recursively determine which setting should be used.
 * @param settings is an array of values ordered by lowest to highest precedence
 * @param defaultVal is treated the same as if setting is not set. it has low precedence
 *
 * Example: ['_step', '_runtime', '_step'] => returns _runtime
 */
export const getCascadingSetting = (settings: any[], defaultVal?: any): any => {
  const settingsLen = settings?.length ?? 0;
  if (settingsLen === 0) {
    return defaultVal;
  }

  if (settingsLen === 1) {
    return settings ?? defaultVal;
  }

  const parentSetting = settings[0];
  const childSetting = settings[1];
  const cascadingValue = isSettingActive(childSetting, defaultVal)
    ? childSetting
    : parentSetting;

  const nextSettings = settings.slice(2);
  if (nextSettings.length === 0) {
    return cascadingValue ?? defaultVal;
  }

  return getCascadingSetting([cascadingValue].concat(nextSettings), defaultVal);
};

/**
 * resolveNullableCascade resolves a nullable numeric setting from a cascade of
 * settings levels. Values are ordered from lowest to highest precedence
 * (matching `getCascadingSetting`). `null` at any level means "explicitly
 * cleared — use auto-scale." `undefined` means "not set — inherit from parent."
 *
 * NOTE: This differs from `getCascadingSetting`, which treats both `null` and
 * `undefined` as "not set." Use this function for fields where a user can
 * explicitly clear a value (e.g. axis range bounds); use `getCascadingSetting`
 * for fields where clearing and never-set are equivalent (e.g. booleans/enums).
 */
export const resolveNullableCascade = <T>(
  ...values: Array<T | null | undefined>
): T | undefined => {
  for (let i = values.length - 1; i >= 0; i--) {
    const value = values[i];
    if (value === null) {
      return undefined;
    }
    if (value != null) {
      return value;
    }
  }

  return undefined;
};
